import { useEffect, useMemo, useState } from "react";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { Button } from "@/components/ui/button";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

import { Bell, Upload } from "lucide-react";

type BadgeGroup = "practice" | "community" | "balance";
type BadgeMetric =
  | "totalDietDays"
  | "totalMeditations"
  | "totalBreathSessions"
  | "totalRituals"
  | "totalTasks"
  | "totalPosts"
  | "totalEnergySent"
  | "totalHealingSent"
  | "fullBalanceDays";

type ProgressBadge = {
  key: string;
  group: BadgeGroup;
  category: string;
  label: string;
  description: string;
  metric: BadgeMetric;
  threshold: number;
  imageUrl?: string;
};

type ProgressScoring = {
  communityCap: number;
  dailyBalanceBonus: number;
  socialComboBonus: number;
};

type ProgressRank = {
  key: string;
  label: string;
  minXp: number;
  imageUrl?: string;
};

type ProgressAuraLevel = {
  key: string;
  label: string;
  minScore: number;
  imageUrl?: string;
};

type ProgressConfigResponse = {
  ok: true;
  badges: ProgressBadge[];
  progressConfig: {
    badges: ProgressBadge[];
    ranks: ProgressRank[];
    auraLevels: ProgressAuraLevel[];
    scoring: ProgressScoring;
  };
};

async function fetchProgressConfig(): Promise<ProgressConfigResponse> {
  const res = await adminFetch("/api/admin/numeroloji/rozetler");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Rozet ayarları alınamadı.");
  }
  return json as ProgressConfigResponse;
}

async function saveBadges(payload: { badges: ProgressBadge[]; ranks: ProgressRank[]; auraLevels: ProgressAuraLevel[] }) {
  const normalizedPayload = {
    badges: payload.badges.map((item) => ({
      ...item,
      imageUrl: String((item as any).imageUrl ?? (item as any).image_url ?? (item as any).iconUrl ?? (item as any).icon_url ?? (item as any).image ?? "").trim() || undefined,
    })),
    ranks: payload.ranks.map((item) => ({
      ...item,
      imageUrl: String((item as any).imageUrl ?? (item as any).image_url ?? (item as any).iconUrl ?? (item as any).icon_url ?? (item as any).image ?? "").trim() || undefined,
    })),
    auraLevels: payload.auraLevels.map((item) => ({
      ...item,
      imageUrl: String((item as any).imageUrl ?? (item as any).image_url ?? (item as any).iconUrl ?? (item as any).icon_url ?? (item as any).image ?? "").trim() || undefined,
    })),
  };

  const res = await adminFetch("/api/admin/numeroloji/rozetler", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(normalizedPayload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Rozetler kaydedilemedi.");
  }
  return json as { ok: true; badges: ProgressBadge[]; ranks: ProgressRank[]; auraLevels: ProgressAuraLevel[] };
}

async function saveScoring(scoring: ProgressScoring) {
  const res = await adminFetch("/api/admin/numeroloji/scoring", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scoring }),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Puanlama ayarları kaydedilemedi.");
  }
  return json as { ok: true; scoring: ProgressScoring };
}

async function uploadBadgeImage(input: { badgeKey: string; category: string; dataBase64: string; contentType: string }) {
  const res = await adminFetch("/api/admin/numeroloji/rozetler/image", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Görsel yüklenemedi.");
  }
  return json as { ok: true; url: string };
}

function toDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Dosya okunamadı."));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsDataURL(file);
  });
}

const metricLabels: Record<BadgeMetric, string> = {
  totalDietDays: "Toplam diyet günü",
  totalMeditations: "Toplam meditasyon",
  totalBreathSessions: "Toplam nefes seansı",
  totalRituals: "Toplam ritüel",
  totalTasks: "Toplam görev",
  totalPosts: "Toplam paylaşım",
  totalEnergySent: "Toplam enerji gönderimi",
  totalHealingSent: "Toplam şifa gönderimi",
  fullBalanceDays: "Tam denge günü",
};

const groupLabels: Record<BadgeGroup, string> = {
  practice: "Pratik",
  community: "Topluluk",
  balance: "Denge",
};

export default function AdminNumerolojiBadges() {
  const queryClient = useQueryClient();

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const configQuery = useQuery({
    queryKey: ["admin-numeroloji-rozetler"],
    queryFn: fetchProgressConfig,
    enabled: authenticated,
    staleTime: 20_000,
  });

  const [badges, setBadges] = useState<ProgressBadge[]>([]);
  const [ranks, setRanks] = useState<ProgressRank[]>([]);
  const [auraLevels, setAuraLevels] = useState<ProgressAuraLevel[]>([]);
  const [scoring, setScoring] = useState<ProgressScoring>({
    communityCap: 35,
    dailyBalanceBonus: 5,
    socialComboBonus: 5,
  });

  useEffect(() => {
    if (!configQuery.data) return;

    const normalizeImageUrl = (item: any) => String(item?.imageUrl ?? item?.image_url ?? item?.iconUrl ?? item?.icon_url ?? item?.image ?? "").trim() || undefined;

    setBadges(
      Array.isArray(configQuery.data.progressConfig?.badges)
        ? configQuery.data.progressConfig.badges.map((item) => ({ ...item, imageUrl: normalizeImageUrl(item) }))
        : [],
    );
    setRanks(
      Array.isArray(configQuery.data.progressConfig?.ranks)
        ? configQuery.data.progressConfig.ranks.map((item) => ({ ...item, imageUrl: normalizeImageUrl(item) }))
        : [],
    );
    setAuraLevels(
      Array.isArray(configQuery.data.progressConfig?.auraLevels)
        ? configQuery.data.progressConfig.auraLevels.map((item) => ({ ...item, imageUrl: normalizeImageUrl(item) }))
        : [],
    );
    setScoring(configQuery.data.progressConfig?.scoring ?? { communityCap: 35, dailyBalanceBonus: 5, socialComboBonus: 5 });
  }, [configQuery.data]);

  const saveBadgesMutation = useMutation({
    mutationFn: (payload: { badges: ProgressBadge[]; ranks: ProgressRank[]; auraLevels: ProgressAuraLevel[] }) => saveBadges(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-rozetler"] });
    },
  });

  const saveScoringMutation = useMutation({
    mutationFn: (nextScoring: ProgressScoring) => saveScoring(nextScoring),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-rozetler"] });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: uploadBadgeImage,
  });

  const grouped = useMemo(() => {
    const map = new Map<BadgeGroup, ProgressBadge[]>();
    map.set("practice", []);
    map.set("community", []);
    map.set("balance", []);

    for (const badge of badges) {
      const arr = map.get(badge.group) ?? [];
      arr.push(badge);
      map.set(badge.group, arr);
    }

    return map;
  }, [badges]);

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  const updateBadge = (index: number, patch: Partial<ProgressBadge>) => {
    setBadges((prev) => prev.map((item, idx) => (idx === index ? { ...item, ...patch } : item)));
  };

  const updateRank = (index: number, patch: Partial<ProgressRank>) => {
    setRanks((prev) => prev.map((item, idx) => (idx === index ? { ...item, ...patch } : item)));
  };

  const updateAuraLevel = (index: number, patch: Partial<ProgressAuraLevel>) => {
    setAuraLevels((prev) => prev.map((item, idx) => (idx === index ? { ...item, ...patch } : item)));
  };

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">Rozet ve Puanlama Yönetimi</div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>

              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 space-y-6 p-4 sm:p-6">
          {configQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Yükleniyor...</div>
          ) : configQuery.isError ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {(configQuery.error as Error).message}
            </div>
          ) : (
            <>
              <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">Puanlama Ayarları</h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Topluluk limiti ve bonus puanlarını buradan değiştirebilirsiniz.</p>
                  </div>
                  <Button
                    className="rounded-full"
                    onClick={() => saveScoringMutation.mutate(scoring)}
                    disabled={saveScoringMutation.isPending}
                  >
                    {saveScoringMutation.isPending ? "Kaydediliyor..." : "Puanlamayı Kaydet"}
                  </Button>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Günlük topluluk limiti
                    <input
                      type="number"
                      min={1}
                      className="mt-1 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-700 dark:bg-slate-900"
                      value={scoring.communityCap}
                      onChange={(e) => setScoring((prev) => ({ ...prev, communityCap: Math.max(1, Number(e.target.value || 0)) }))}
                    />
                  </label>

                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Günlük denge bonusu
                    <input
                      type="number"
                      min={0}
                      className="mt-1 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-700 dark:bg-slate-900"
                      value={scoring.dailyBalanceBonus}
                      onChange={(e) => setScoring((prev) => ({ ...prev, dailyBalanceBonus: Math.max(0, Number(e.target.value || 0)) }))}
                    />
                  </label>

                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Sosyal kombin bonusu
                    <input
                      type="number"
                      min={0}
                      className="mt-1 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-700 dark:bg-slate-900"
                      value={scoring.socialComboBonus}
                      onChange={(e) => setScoring((prev) => ({ ...prev, socialComboBonus: Math.max(0, Number(e.target.value || 0)) }))}
                    />
                  </label>
                </div>

                {saveScoringMutation.isError ? (
                  <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {(saveScoringMutation.error as Error).message}
                  </div>
                ) : null}
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">Ana Unvanlar</h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Tohum, Arayıcı gibi seviye unvanlarını ve XP eşiklerini düzenleyin.</p>
                  </div>
                  <Button
                    className="rounded-full"
                    onClick={() => saveBadgesMutation.mutate({ badges, ranks, auraLevels })}
                    disabled={saveBadgesMutation.isPending}
                  >
                    {saveBadgesMutation.isPending ? "Kaydediliyor..." : "Unvanları Kaydet"}
                  </Button>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  {ranks.map((rank, index) => (
                    <div key={rank.key} className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
                      <div className="mb-3 flex items-center gap-3">
                        <div className="grid h-12 w-12 place-items-center overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                          {rank.imageUrl ? <img src={rank.imageUrl} alt={rank.label} className="h-full w-full object-cover" /> : <span className="text-xs text-slate-400">Yok</span>}
                        </div>

                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800">
                          <Upload className="h-3.5 w-3.5" />
                          Görsel Yükle
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;

                              const dataUrl = await toDataUrl(file);
                              const [, dataBase64 = ""] = String(dataUrl).split(",");
                              if (!dataBase64) return;

                              const uploaded = await uploadMutation.mutateAsync({
                                badgeKey: rank.key,
                                category: "rank",
                                contentType: file.type,
                                dataBase64,
                              });

                              const nextRanks = ranks.map((item, idx) => (idx === index ? { ...item, imageUrl: uploaded.url } : item));
                              setRanks(nextRanks);
                              await saveBadgesMutation.mutateAsync({ badges, ranks: nextRanks, auraLevels });
                              e.currentTarget.value = "";
                            }}
                          />
                        </label>
                      </div>

                      <div className="grid gap-2 sm:grid-cols-2">
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                          Sistem anahtarı
                          <input
                            className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-slate-100 px-3 text-xs text-slate-500 outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400"
                            value={rank.key}
                            disabled
                          />
                        </label>

                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                          Min XP
                          <input
                            type="number"
                            min={0}
                            className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs outline-none dark:border-slate-700 dark:bg-slate-950"
                            value={rank.minXp}
                            onChange={(e) => updateRank(index, { minXp: Math.max(0, Number(e.target.value || 0)) })}
                          />
                        </label>
                      </div>

                      <label className="mt-2 block text-xs font-medium text-slate-600 dark:text-slate-300">
                        Unvan adı
                        <input
                          className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs outline-none dark:border-slate-700 dark:bg-slate-950"
                          value={rank.label}
                          onChange={(e) => updateRank(index, { label: e.target.value })}
                        />
                      </label>
                    </div>
                  ))}
                </div>

                {saveBadgesMutation.isError ? (
                  <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {(saveBadgesMutation.error as Error).message}
                  </div>
                ) : null}
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">Aura Seviyeleri</h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Beyaz, Toprak, Zümrüt gibi aura seviyelerinin adını ve min skorunu düzenleyin.</p>
                  </div>
                  <Button
                    className="rounded-full"
                    onClick={() => saveBadgesMutation.mutate({ badges, ranks, auraLevels })}
                    disabled={saveBadgesMutation.isPending}
                  >
                    {saveBadgesMutation.isPending ? "Kaydediliyor..." : "Aurayı Kaydet"}
                  </Button>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  {auraLevels.map((level, index) => (
                    <div key={level.key} className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
                      <div className="mb-3 flex items-center gap-3">
                        <div className="grid h-12 w-12 place-items-center overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                          {level.imageUrl ? <img src={level.imageUrl} alt={level.label} className="h-full w-full object-cover" /> : <span className="text-xs text-slate-400">Yok</span>}
                        </div>

                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800">
                          <Upload className="h-3.5 w-3.5" />
                          Görsel Yükle
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;

                              const dataUrl = await toDataUrl(file);
                              const [, dataBase64 = ""] = String(dataUrl).split(",");
                              if (!dataBase64) return;

                              const uploaded = await uploadMutation.mutateAsync({
                                badgeKey: level.key,
                                category: "aura",
                                contentType: file.type,
                                dataBase64,
                              });

                              const nextAuraLevels = auraLevels.map((item, idx) => (idx === index ? { ...item, imageUrl: uploaded.url } : item));
                              setAuraLevels(nextAuraLevels);
                              await saveBadgesMutation.mutateAsync({ badges, ranks, auraLevels: nextAuraLevels });
                              e.currentTarget.value = "";
                            }}
                          />
                        </label>
                      </div>

                      <div className="grid gap-2 sm:grid-cols-2">
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                          Sistem anahtarı
                          <input
                            className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-slate-100 px-3 text-xs text-slate-500 outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400"
                            value={level.key}
                            disabled
                          />
                        </label>

                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                          Min skor
                          <input
                            type="number"
                            min={0}
                            className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs outline-none dark:border-slate-700 dark:bg-slate-950"
                            value={level.minScore}
                            onChange={(e) => updateAuraLevel(index, { minScore: Math.max(0, Number(e.target.value || 0)) })}
                          />
                        </label>
                      </div>

                      <label className="mt-2 block text-xs font-medium text-slate-600 dark:text-slate-300">
                        Aura adı
                        <input
                          className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs outline-none dark:border-slate-700 dark:bg-slate-950"
                          value={level.label}
                          onChange={(e) => updateAuraLevel(index, { label: e.target.value })}
                        />
                      </label>
                    </div>
                  ))}
                </div>

                {saveBadgesMutation.isError ? (
                  <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {(saveBadgesMutation.error as Error).message}
                  </div>
                ) : null}
              </section>

              <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">Rozetler</h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Rozet adlarını, açıklamalarını, eşiklerini ve görsellerini düzenleyin.</p>
                  </div>
                  <Button
                    className="rounded-full"
                    onClick={() => saveBadgesMutation.mutate({ badges, ranks, auraLevels })}
                    disabled={saveBadgesMutation.isPending}
                  >
                    {saveBadgesMutation.isPending ? "Kaydediliyor..." : "Rozetleri Kaydet"}
                  </Button>
                </div>

                <div className="space-y-6">
                  {(["practice", "community", "balance"] as BadgeGroup[]).map((group) => {
                    const rows = grouped.get(group) ?? [];

                    return (
                      <div key={group} className="rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                        <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">{groupLabels[group]}</h3>

                        <div className="grid gap-3 md:grid-cols-2">
                          {rows.map((badge) => {
                            const index = badges.findIndex((item) => item.key === badge.key);

                            return (
                              <div key={badge.key} className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
                                <div className="mb-3 flex items-center gap-3">
                                  <div className="grid h-12 w-12 place-items-center overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                                    {badge.imageUrl ? <img src={badge.imageUrl} alt={badge.label} className="h-full w-full object-cover" /> : <span className="text-xs text-slate-400">Yok</span>}
                                  </div>

                                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800">
                                    <Upload className="h-3.5 w-3.5" />
                                    Görsel Yükle
                                    <input
                                      type="file"
                                      accept="image/png,image/jpeg,image/webp,image/gif"
                                      className="hidden"
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file || index < 0) return;

                                        const dataUrl = await toDataUrl(file);
                                        const [, dataBase64 = ""] = String(dataUrl).split(",");
                                        if (!dataBase64) return;

                                        const uploaded = await uploadMutation.mutateAsync({
                                          badgeKey: badge.key,
                                          category: badge.category,
                                          contentType: file.type,
                                          dataBase64,
                                        });

                                        const nextBadges = badges.map((item, idx) => (idx === index ? { ...item, imageUrl: uploaded.url } : item));
                                        setBadges(nextBadges);
                                        await saveBadgesMutation.mutateAsync({ badges: nextBadges, ranks, auraLevels });
                                        e.currentTarget.value = "";
                                      }}
                                    />
                                  </label>
                                </div>

                                <div className="grid gap-2">
                                  <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                                    Rozet adı
                                    <input
                                      className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs outline-none dark:border-slate-700 dark:bg-slate-950"
                                      value={badge.label}
                                      onChange={(e) => index >= 0 && updateBadge(index, { label: e.target.value })}
                                    />
                                  </label>

                                  <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                                    Açıklama
                                    <input
                                      className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs outline-none dark:border-slate-700 dark:bg-slate-950"
                                      value={badge.description}
                                      onChange={(e) => index >= 0 && updateBadge(index, { description: e.target.value })}
                                    />
                                  </label>

                                  <div className="grid grid-cols-2 gap-2">
                                    <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                                      Eşik
                                      <input
                                        type="number"
                                        min={0}
                                        className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs outline-none dark:border-slate-700 dark:bg-slate-950"
                                        value={badge.threshold}
                                        onChange={(e) => index >= 0 && updateBadge(index, { threshold: Math.max(0, Number(e.target.value || 0)) })}
                                      />
                                    </label>

                                    <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                                      Metrik
                                      <select
                                        className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs outline-none dark:border-slate-700 dark:bg-slate-950"
                                        value={badge.metric}
                                        onChange={(e) => index >= 0 && updateBadge(index, { metric: e.target.value as BadgeMetric })}
                                      >
                                        {(Object.keys(metricLabels) as BadgeMetric[]).map((metric) => (
                                          <option key={metric} value={metric}>
                                            {metricLabels[metric]}
                                          </option>
                                        ))}
                                      </select>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {saveBadgesMutation.isError ? (
                  <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {(saveBadgesMutation.error as Error).message}
                  </div>
                ) : null}

                {uploadMutation.isError ? (
                  <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {(uploadMutation.error as Error).message}
                  </div>
                ) : null}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
